import { SCHEMA_VERSION } from './schema';

/** One pure step between consecutive schema versions (GDD §6.4): `migrateV1toV2`, `migrateV2toV3`... */
export interface Migration {
  from: number;
  to: number;
  name: string;
  /** Receives its own copy of the document and returns a new object; never touches storage. */
  run(doc: unknown): unknown;
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * v1 to v2 (issue #138, GDD §6.4): every record of a completed level gets `assisted: false`, because nothing was played
 * with the assist mode before it existed. Empty slots (`null`) and a document without slots go through unchanged.
 */
export function migrateV1toV2(doc: unknown): unknown {
  const source = doc as Record<string, unknown>;
  const slots: Record<string, unknown> = {};
  for (const [id, slot] of Object.entries(isRecord(source.slots) ? source.slots : {})) {
    if (!isRecord(slot) || !isRecord(slot.completedLevels)) {
      slots[id] = slot;
      continue;
    }
    const completedLevels: Record<string, unknown> = {};
    for (const [level, record] of Object.entries(slot.completedLevels)) completedLevels[level] = isRecord(record) ? { ...record, assisted: false } : record;
    slots[id] = { ...slot, completedLevels };
  }
  return { ...source, schemaVersion: 2, slots };
}

/** The chain of one pure step per version (GDD §6.4). */
export const MIGRATIONS: readonly Migration[] = [{ from: 1, to: 2, name: 'migrateV1toV2', run: migrateV1toV2 }];

/** The document cannot be brought to the current version; the loader treats it as corrupt (#48). */
export class MigrationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MigrationError';
  }
}

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Runs the chain from the version of the document up to `target`, one registered step per version. The
 * input is never mutated. Throws MigrationError for an invalid or future version, a missing step, a step
 * that throws or a step that does not return the version it declares.
 */
export function migrate(
  doc: unknown,
  registry: readonly Migration[] = MIGRATIONS,
  target: number = SCHEMA_VERSION,
): { doc: unknown; ran: string[] } {
  if (!isObject(doc)) throw new MigrationError('document is not an object');
  const declared = doc.schemaVersion;
  if (typeof declared !== 'number' || !Number.isInteger(declared) || declared < 0) {
    throw new MigrationError('invalid schema version');
  }
  let version: number = declared;
  if (version > target) throw new MigrationError(`unsupported future version ${version}`);

  let current: unknown = structuredClone(doc);
  const ran: string[] = [];
  while (version < target) {
    const step = registry.find((candidate) => candidate.from === version);
    if (!step) throw new MigrationError(`no migration from version ${version}`);
    let next: unknown;
    try {
      next = step.run(structuredClone(current));
    } catch (error) {
      throw new MigrationError(`${step.name} failed: ${error instanceof Error ? error.message : String(error)}`);
    }
    if (!isObject(next) || next.schemaVersion !== step.to) {
      throw new MigrationError(`${step.name} did not produce version ${step.to}`);
    }
    current = next;
    version = step.to;
    ran.push(step.name);
  }
  return { doc: current, ran };
}
